// The image viewer — ios/Sources/Views/ImageViewerView.swift for a browser window: the picture on
// a dark scrim fitted to the window, ←/→ through the conversation's pictures in order (the Mac
// pages through the post's; the web walks every picture loaded in the pane, starting at the one
// clicked), zoom by click / wheel / pinch with a pan that never leaves the picture, Esc or ✕ to
// close, download, open the original in a tab, and the file's name, author and time.
//
// Economy (Android's ImageViewer rule, kept): the PREVIEW is shown first; the ORIGINAL is fetched
// only when the reader zooms in (GIFs always use the original — a preview of a GIF is a still).
// It mounts itself on first use (its own React root over the page), so no screen has to host it.
import { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createRoot, type Root } from 'react-dom/client';
import { ImageViewerZoom as Z, type Size, type ZoomView } from '../../../core/imageViewerZoom';
import { Icon } from '../../../design/icons';
import { locale, t, tf } from '../../../i18n/i18n';
import { mediaUrls } from '../../media';
import { isLocalId, localUrl } from './localFiles';
import { copyImageAndSay, saveImageAs } from './imageClipboard';
import './attachments.css';

export interface LightboxItem {
  fileId: string;
  name: string;
  author: string;
  at: number;
  width: number;
  height: number;
  mimeType: string;
}

let host: HTMLDivElement | null = null;
let root: Root | null = null;
let opened = 0;

export function openLightbox(items: LightboxItem[], index: number): void {
  if (!items.length) return;
  if (!host) {
    host = document.createElement('div');
    host.className = 'lightbox-host';
    document.body.appendChild(host);
    root = createRoot(host);
  }
  const returnFocus = document.activeElement as HTMLElement | null;
  opened += 1;
  root!.render(
    <Lightbox
      key={opened}
      items={items}
      start={Math.max(0, Math.min(items.length - 1, index))}
      onClose={() => {
        root?.render(null);
        returnFocus?.focus?.();
      }}
    />,
  );
}

/** The items for a click inside a pane: every picture tile of that pane, in reading order. */
export function lightboxItemsAround(tile: HTMLElement): { items: LightboxItem[]; index: number } {
  const scope = tile.closest('.conversation-pane') ?? tile.ownerDocument;
  const tiles = Array.from(scope.querySelectorAll<HTMLElement>('[data-lb-file]'));
  const items = tiles.map((el) => ({
    fileId: el.dataset.lbFile ?? '',
    name: el.dataset.lbName ?? '',
    author: el.dataset.lbAuthor ?? '',
    at: Number(el.dataset.lbAt ?? 0),
    width: Number(el.dataset.lbW ?? 0),
    height: Number(el.dataset.lbH ?? 0),
    mimeType: el.dataset.lbMime ?? '',
  }));
  return { items, index: Math.max(0, tiles.indexOf(tile)) };
}

function isGif(item: LightboxItem): boolean {
  return item.mimeType === 'image/gif' || /\.gif$/i.test(item.name);
}

function when(ms: number): string {
  if (!ms) return '';
  try {
    return new Intl.DateTimeFormat(locale(), { dateStyle: 'medium', timeStyle: 'short' }).format(new Date(ms));
  } catch {
    return '';
  }
}

function Lightbox({ items, start, onClose }: { items: LightboxItem[]; start: number; onClose(): void }) {
  const [index, setIndex] = useState(start);
  const item = items[index];
  const [view, setView] = useState<ZoomView>(Z.IDENTITY);
  const [natural, setNatural] = useState<Size | null>(null);
  const [hiRes, setHiRes] = useState(false);
  const [failed, setFailed] = useState(false);
  const [stageSize, setStageSize] = useState<Size>({ w: window.innerWidth, h: window.innerHeight });
  const stage = useRef<HTMLDivElement>(null);
  const closeButton = useRef<HTMLButtonElement>(null);
  // «Copiar imagen» (toolbar + the picture's own right-click menu). The viewer covers the app's
  // toast, so it says the result itself.
  const [menuAt, setMenuAt] = useState<{ x: number; y: number } | null>(null);
  const [notice, setNotice] = useState<{ text: string; tone: 'info' | 'error' } | null>(null);
  const noticeTimer = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
  const say = useCallback((text: string, tone: 'info' | 'error' = 'info') => {
    setNotice({ text, tone });
    clearTimeout(noticeTimer.current);
    noticeTimer.current = setTimeout(() => setNotice(null), 3500);
  }, []);
  useEffect(() => () => clearTimeout(noticeTimer.current), []);
  const imageRef = { fileId: item.fileId, name: item.name, mimeType: item.mimeType };
  const copyThis = () => {
    setMenuAt(null);
    void copyImageAndSay(imageRef, say);
  };
  const saveThis = () => {
    setMenuAt(null);
    saveImageAs(imageRef);
  };

  const local = localUrl(item.fileId);
  const gif = isGif(item);
  const src = local ?? (gif || hiRes ? mediaUrls.file(item.fileId) : mediaUrls.preview(item.fileId));
  const known = item.width > 0 && item.height > 0 ? { w: item.width, h: item.height } : natural;
  // A margin around the picture at 1× so it never touches the chrome or the window's edge.
  const fitted = known ? Z.fitSize(known, { w: Math.max(1, stageSize.w - 48), h: Math.max(1, stageSize.h - 32) }) : null;

  // Leaving a page resets its zoom (ImageViewerView: never land mid-zoom on yesterday's pan).
  useEffect(() => {
    setView(Z.IDENTITY);
    setNatural(null);
    setHiRes(false);
    setFailed(false);
    // The neighbours' previews, so ←/→ paints at once.
    for (const n of [items[index + 1], items[index - 1]]) {
      if (n && !localUrl(n.fileId)) new Image().src = isGif(n) ? mediaUrls.file(n.fileId) : mediaUrls.preview(n.fileId);
    }
  }, [index, items]);

  // Zoomed in: the original replaces the preview once it has fully arrived (never a blank swap).
  useEffect(() => {
    if (view.scale <= 1 || hiRes || local || gif) return;
    const img = new Image();
    img.onload = () => setHiRes(true);
    img.src = mediaUrls.file(item.fileId);
  }, [view.scale > 1, hiRes, local, gif, item.fileId]);

  useLayoutEffect(() => {
    const el = stage.current;
    if (!el) return;
    const apply = () => setStageSize({ w: el.clientWidth, h: el.clientHeight });
    apply();
    const ro = new ResizeObserver(apply);
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  useEffect(() => closeButton.current?.focus(), []);

  const go = useCallback((delta: number) => setIndex((i) => Z.step(i, delta, items.length)), [items.length]);
  const zoomBy = useCallback(
    (factor: number) => setView((v) => (fitted ? Z.zoomAt(v, v.scale * factor, { x: 0, y: 0 }, fitted, stageSize) : v)),
    [fitted?.w, fitted?.h, stageSize.w, stageSize.h],
  );

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && menuAt) {
        e.preventDefault();
        e.stopPropagation();
        setMenuAt(null);
      } else if (e.key === 'Escape') {
        e.preventDefault();
        e.stopPropagation();
        onClose();
      } else if (e.key === 'ArrowLeft') {
        e.preventDefault();
        go(-1);
      } else if (e.key === 'ArrowRight') {
        e.preventDefault();
        go(1);
      } else if (e.key === '+' || e.key === '=') {
        e.preventDefault();
        zoomBy(1.5);
      } else if (e.key === '-') {
        e.preventDefault();
        zoomBy(1 / 1.5);
      } else if (e.key === '0') {
        e.preventDefault();
        setView(Z.IDENTITY);
      }
    };
    // Capture: the thread pane's own Esc (close the thread) must not also fire under the viewer.
    window.addEventListener('keydown', onKey, true);
    return () => window.removeEventListener('keydown', onKey, true);
  }, [go, zoomBy, onClose, menuAt]);

  // Wheel / trackpad pinch (ctrl+wheel) zooms at the pointer; it must be non-passive to own it.
  useEffect(() => {
    const el = stage.current;
    if (!el || !fitted) return;
    const onWheel = (e: WheelEvent) => {
      e.preventDefault();
      const r = el.getBoundingClientRect();
      const point = { x: e.clientX - r.left - r.width / 2, y: e.clientY - r.top - r.height / 2 };
      const delta = e.deltaY * (e.deltaMode === 1 ? 40 : 1) * (e.ctrlKey ? 4 : 1);
      setView((v) => Z.zoomAt(v, Z.wheelScale(v.scale, delta), point, fitted, stageSize));
    };
    el.addEventListener('wheel', onWheel, { passive: false });
    return () => el.removeEventListener('wheel', onWheel);
  }, [fitted?.w, fitted?.h, stageSize.w, stageSize.h]);

  // Pointer gestures: a click toggles zoom (on the picture) or closes (on the scrim, at 1×); a drag
  // pans a zoomed picture; two fingers pinch; a touch swipe at 1× pages.
  const pointers = useRef(new Map<number, { x: number; y: number }>());
  const gesture = useRef<{ x: number; y: number; view: ZoomView; moved: boolean; onImage: boolean; dist: number; mid: { x: number; y: number } | null; touch: boolean } | null>(null);
  const rel = (x: number, y: number) => {
    const r = stage.current!.getBoundingClientRect();
    return { x: x - r.left - r.width / 2, y: y - r.top - r.height / 2 };
  };

  const onPointerDown = (e: React.PointerEvent) => {
    if ((e.target as HTMLElement).closest('button, a')) return;
    if (e.button === 2) return; // the right-click opens the picture's menu, never a zoom/close
    if (menuAt) {
      setMenuAt(null);
      return;
    }
    stage.current?.setPointerCapture(e.pointerId);
    pointers.current.set(e.pointerId, { x: e.clientX, y: e.clientY });
    const pts = [...pointers.current.values()];
    if (pts.length === 2) {
      const [a, b] = pts;
      gesture.current = { x: 0, y: 0, view, moved: true, onImage: true, dist: Math.hypot(a.x - b.x, a.y - b.y), mid: rel((a.x + b.x) / 2, (a.y + b.y) / 2), touch: true };
    } else {
      gesture.current = { x: e.clientX, y: e.clientY, view, moved: false, onImage: (e.target as HTMLElement).tagName === 'IMG', dist: 0, mid: null, touch: e.pointerType === 'touch' };
    }
  };

  const onPointerMove = (e: React.PointerEvent) => {
    const g = gesture.current;
    if (!g || !pointers.current.has(e.pointerId) || !fitted) return;
    pointers.current.set(e.pointerId, { x: e.clientX, y: e.clientY });
    const pts = [...pointers.current.values()];
    if (pts.length >= 2 && g.mid) {
      const [a, b] = pts;
      const dist = Math.hypot(a.x - b.x, a.y - b.y);
      setView(Z.zoomAt(g.view, g.view.scale * (dist / Math.max(1, g.dist)), g.mid, fitted, stageSize));
      return;
    }
    const dx = e.clientX - g.x;
    const dy = e.clientY - g.y;
    if (!g.moved && Math.hypot(dx, dy) > 4) g.moved = true;
    if (g.moved && g.view.scale > 1) setView(Z.clampPan({ ...g.view, x: g.view.x + dx, y: g.view.y + dy }, fitted, stageSize));
  };

  const onPointerUp = (e: React.PointerEvent) => {
    const g = gesture.current;
    pointers.current.delete(e.pointerId);
    if (pointers.current.size > 0) return;
    gesture.current = null;
    if (!g || g.mid) return;
    const dx = e.clientX - g.x;
    if (g.moved) {
      if (g.touch && g.view.scale <= 1 && Math.abs(dx) > 60) go(dx < 0 ? 1 : -1);
      return;
    }
    if (g.onImage && fitted) setView((v) => Z.toggle(v, rel(e.clientX, e.clientY), fitted, stageSize));
    else if (!g.onImage && view.scale <= 1) onClose();
  };

  const subtitle = [item.author, when(item.at)].filter(Boolean).join(' · ');
  const transform = `translate(${view.x}px, ${view.y}px) scale(${view.scale})`;

  return (
    <div className="lightbox" role="dialog" aria-modal="true" aria-label={t('web.viewer.label')}>
      <header className="lightbox-bar">
        <div className="lightbox-title">
          <strong>{item.name}</strong>
          {subtitle ? <span>{subtitle}</span> : null}
        </div>
        {items.length > 1 ? <span className="lightbox-counter">{tf('web.viewer.counter', index + 1, items.length)}</span> : null}
        <div className="lightbox-actions">
          <button className="lightbox-button" data-viewer="copy-image" onClick={copyThis} disabled={failed} title={t('web.image.copy')} aria-label={t('web.image.copy')}>
            <Icon name="copy" size={18} />
          </button>
          <button className="lightbox-button" onClick={() => zoomBy(1 / 1.5)} disabled={view.scale <= 1} title={t('web.viewer.zoomOut')} aria-label={t('web.viewer.zoomOut')}>
            <Icon name="zoomOut" size={18} />
          </button>
          <button className="lightbox-button" onClick={() => zoomBy(1.5)} disabled={view.scale >= Z.MAX_SCALE} title={t('web.viewer.zoomIn')} aria-label={t('web.viewer.zoomIn')}>
            <Icon name="zoomIn" size={18} />
          </button>
          <a className="lightbox-button" href={local ?? mediaUrls.file(item.fileId, true)} download={item.name} title={t('web.viewer.download')} aria-label={t('web.viewer.download')}>
            <Icon name="download" size={18} />
          </a>
          <a className="lightbox-button" href={isLocalId(item.fileId) && local ? local : mediaUrls.file(item.fileId)} target="_blank" rel="noopener noreferrer" title={t('web.viewer.openOriginal')} aria-label={t('web.viewer.openOriginal')}>
            <Icon name="external" size={18} />
          </a>
          <button ref={closeButton} className="lightbox-button" onClick={onClose} title={t('common.close')} aria-label={t('common.close')}>
            <Icon name="x" size={18} />
          </button>
        </div>
      </header>
      <div
        className={`lightbox-stage${view.scale > 1 ? ' zoomed' : ''}`}
        ref={stage}
        onPointerDown={onPointerDown}
        onPointerMove={onPointerMove}
        onPointerUp={onPointerUp}
        onPointerCancel={onPointerUp}
        onContextMenu={(e) => {
          // On the picture: OUR menu (the browser's would copy the PREVIEW, not the original).
          if ((e.target as HTMLElement).tagName !== 'IMG') return;
          e.preventDefault();
          setMenuAt({ x: e.clientX, y: e.clientY });
        }}
      >
        {failed ? (
          <div className="lightbox-failed" role="alert">
            {t('web.viewer.loadFailed')}
          </div>
        ) : (
          <img
            key={item.fileId}
            className="lightbox-image"
            src={src}
            alt={item.name}
            draggable={false}
            data-scale={view.scale}
            style={fitted ? { width: fitted.w, height: fitted.h, transform } : { maxWidth: '100%', maxHeight: '100%', transform }}
            onLoad={(e) => {
              const img = e.currentTarget;
              if (!natural && img.naturalWidth) setNatural({ w: img.naturalWidth, h: img.naturalHeight });
            }}
            onError={() => {
              // A preview the server could not make (no has_preview_image) → the original, once.
              if (!hiRes && !local) setHiRes(true);
              else setFailed(true);
            }}
          />
        )}
        {index > 0 ? (
          <button className="lightbox-nav prev" onClick={() => go(-1)} title={t('web.viewer.previous')} aria-label={t('web.viewer.previous')}>
            <Icon name="chevronLeft" size={22} />
          </button>
        ) : null}
        {index < items.length - 1 ? (
          <button className="lightbox-nav next" onClick={() => go(1)} title={t('web.viewer.next')} aria-label={t('web.viewer.next')}>
            <Icon name="chevronRight" size={22} />
          </button>
        ) : null}
      </div>
      {menuAt ? (
        <div
          className="popover message-menu lightbox-menu"
          role="menu"
          style={{ position: 'fixed', left: Math.min(menuAt.x, window.innerWidth - 238), top: Math.min(menuAt.y, window.innerHeight - 100), width: 230 }}
          onPointerDown={(e) => e.stopPropagation()}
        >
          <button className="menu-item" role="menuitem" data-menu="copy-image" onClick={copyThis}>
            <Icon name="copy" size={16} /> {t('web.image.copy')}
          </button>
          <button className="menu-item" role="menuitem" data-menu="save-image" onClick={saveThis}>
            <Icon name="download" size={16} /> {t('web.image.saveAs')}
          </button>
        </div>
      ) : null}
      {notice ? (
        <div className={`lightbox-notice${notice.tone === 'error' ? ' error' : ''}`} role="status">
          {notice.text}
        </div>
      ) : null}
    </div>
  );
}
